'use client';

/**
 * @fileoverview Provider module for query provider.
 *
 * IMPORTANT: QueryClient is created inside useState so each component tree gets
 * its own isolated client.
 *
 * TENANT ISOLATION: On workspace switch, the QueryClient is fully cleared
 * (nuclear clear) to guarantee zero cross-workspace data leakage.
 */
import { QueryClientProvider } from '@tanstack/react-query';
import { createQueryClient } from '@/lib/queryClient';
import { type ReactNode, useEffect, useState } from 'react';

export function QueryProvider({ children }: { children: ReactNode }) {
  // ✅ CORRECTNESS: Instantiated inside useState — stable reference per component tree.
  const [queryClient] = useState(() => createQueryClient());

  // ─────────────────────────────────────────────────────────────────────────
  // Nuclear cache clear on workspace switch.
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleWorkspaceChange = () => {
      // Cancel all in-flight queries so their stale responses never land
      void queryClient.cancelQueries();
      // Remove all cached data — the nuclear option
      queryClient.clear();
    };

    window.addEventListener('postbrix:workspace-changed', handleWorkspaceChange);
    return () => window.removeEventListener('postbrix:workspace-changed', handleWorkspaceChange);
  }, [queryClient]);

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}

